/**
 * <ReportScreenContextPanel /> — Painel colapsavel exibindo o contexto tecnico
 * (URL, componente, versao, tenant, breadcrumbs, console errors, last_error)
 * que sera anexado automaticamente ao chamado.
 *
 * Spec: CHAMADOS_VISIBILIDADE_SPEC §9.3.6.
 *
 * Recebe `context: ScreenContext` (formato real do useScreenContext —
 * route_path/route_label/component/viewport.{width,height}/last_error.*).
 */

import { useState } from 'react'
import {
  Stack,
  Text,
  Code,
  Group,
  Collapse,
  ActionIcon,
  Box,
} from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import type { ScreenContext } from '@/hooks/useScreenContext'

interface ReportScreenContextPanelProps {
  context: ScreenContext
  defaultOpened?: boolean
}

export function ReportScreenContextPanel({
  context,
  defaultOpened = false,
}: ReportScreenContextPanelProps) {
  const [opened, setOpened] = useState(defaultOpened)

  return (
    <Stack gap="xs">
      <Group
        gap={4}
        style={{ cursor: 'pointer' }}
        onClick={() => setOpened(!opened)}
        role="button"
        aria-expanded={opened}
        aria-label="Alternar contexto técnico"
      >
        <ActionIcon variant="subtle" size="sm" component="span">
          {opened ? (
            <IconChevronDown size={14} />
          ) : (
            <IconChevronRight size={14} />
          )}
        </ActionIcon>
        <Text size="sm" fw={500}>
          Contexto técnico (anexado automaticamente)
        </Text>
      </Group>
      <Collapse in={opened}>
        <Box
          p="sm"
          bg="var(--mantine-color-dark-7)"
          style={{ borderRadius: 4, fontFamily: 'monospace' }}
        >
          <Stack gap={6} pl="md">
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                URL:
              </Text>
              <Code>{context.route_path}</Code>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Componente:
              </Text>
              <Code>{context.component ?? '—'}</Code>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Versão app:
              </Text>
              <Code>{context.app_version}</Code>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Tenant:
              </Text>
              <Code>{context.selected_tenant_id ?? 'n/a'}</Code>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                User:
              </Text>
              <Code>{context.user_id ?? 'anonimo'}</Code>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Viewport:
              </Text>
              <Code>
                {context.viewport.width}x{context.viewport.height}
              </Code>
            </Group>
            {context.last_error && (
              <Stack gap={4}>
                <Text size="xs" c="red.7" fw={500}>
                  Erro detectado:
                </Text>
                <Code
                  block
                  style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}
                >
                  {context.last_error.message}
                </Code>
                {context.last_error.component_stack && (
                  <Code
                    block
                    style={{
                      fontSize: 10,
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {context.last_error.component_stack}
                  </Code>
                )}
              </Stack>
            )}
            {context.breadcrumbs.length > 0 && (
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Últimas rotas:
                </Text>
                {context.breadcrumbs.slice(-5).map((b, i) => (
                  <Code key={i} style={{ fontSize: 11 }}>
                    {b}
                  </Code>
                ))}
              </Stack>
            )}
            {context.console_errors.length > 0 && (
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Console errors recentes:
                </Text>
                {context.console_errors.slice(-3).map((e, i) => (
                  <Code
                    key={i}
                    style={{
                      fontSize: 10,
                      color: 'var(--mantine-color-red-7)',
                    }}
                  >
                    {e}
                  </Code>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Stack>
  )
}
